import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, Shield, CheckCircle, Mail } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';

const PrivacyNotice = () => {
  const dataTypes = [
    'Contact details',
    'Referral and enquiry information',
    'Recruitment information',
    'Service delivery information (where applicable)'
  ];

  const uses = [
    'To respond to enquiries and referrals',
    'To deliver services',
    'To meet legal and regulatory obligations',
    'To improve service quality'
  ];

  const rights = [
    'Access your personal data',
    'Rectify inaccurate information',
    'Restrict processing of your data',
    'Object to processing',
    'Data portability (where applicable)',
    'Withdraw consent (where applicable)'
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#E8F4FC] to-[#F0EDF7] py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <Lock className="w-16 h-16 text-[#4A90E2] mx-auto" />
            <h1 className="heading-hero">Privacy Notice</h1>
            <p className="body-large">
              Protecting personal data and respecting privacy in accordance with UK GDPR and Data Protection Act 2018.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <p className="body-large text-center mb-12">
              Three Spheres Healthcare is committed to protecting personal data and respecting privacy in accordance with the <strong>UK General Data Protection Regulation (UK GDPR)</strong> and <strong>Data Protection Act 2018</strong>.
            </p>

            {/* What We Collect */}
            <div className="mb-12">
              <h2 className="heading-2 mb-6">What Information We Collect</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {dataTypes.map((type, index) => (
                  <Card key={index} className="p-4 border-0 bg-[#E8F4FC]">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="text-[#4A90E2] flex-shrink-0" size={20} />
                      <span className="text-gray-800">{type}</span>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            {/* How We Use It */}
            <div className="mb-12">
              <h2 className="heading-2 mb-6">How We Use Your Information</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {uses.map((use, index) => (
                  <Card key={index} className="p-4 border-0 bg-[#F0EDF7]">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="text-[#7B68BE] flex-shrink-0" size={20} />
                      <span className="text-gray-800">{use}</span>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            {/* Lawful Basis */}
            <div className="mb-12">
              <h2 className="heading-2 mb-6">Lawful Basis</h2>
              <Card className="p-8 border-0">
                <p className="body-medium text-gray-700">
                  Information is processed under lawful bases including <strong>consent</strong>, <strong>contractual necessity</strong>, and <strong>legal obligation</strong>.
                </p>
              </Card>
            </div>

            {/* Your Rights */}
            <div className="mb-12">
              <h2 className="heading-2 mb-6">Your Rights</h2>
              <p className="body-medium text-gray-700 mb-6">
                You have the right to:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                {rights.map((right, index) => (
                  <Card key={index} className="p-4 border-0 bg-[#E8F4FC]">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="text-[#4A90E2] flex-shrink-0" size={20} />
                      <span className="text-gray-800">{right}</span>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            {/* Contact */}
            <div className="bg-gradient-to-br from-[#E8F4FC] to-[#F0EDF7] rounded-2xl p-8 text-center">
              <Mail className="w-12 h-12 text-[#4A90E2] mx-auto mb-4" />
              <h3 className="heading-3 mb-4">Data Protection Queries</h3>
              <p className="body-medium text-gray-700 mb-4">
                For data protection queries, please contact:
              </p>
              <a 
                href="mailto:info@threesphereshealthcare.org"
                className="text-[#4A90E2] font-semibold hover:underline"
              >
                info@threesphereshealthcare.org
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-gradient-to-r from-[#4A90E2] to-[#7B68BE] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <Shield className="w-12 h-12 mx-auto" />
            <h2 className="heading-2 text-white">Your Privacy Matters</h2>
            <p className="body-large text-white/90">
              We are committed to protecting your personal information at all times.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[#4A90E2] hover:bg-gray-100">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyNotice;
